/**
 * How to measure time on the destination, and why it takes a snippet.
 *
 * This panel exists because the honest answer to "how long did they stay?" is
 * uncomfortable and needs saying rather than hiding behind a number. Once the
 * redirect has happened the visitor is on a site this server does not serve,
 * and no browser will report on such a page — so the only way to know is for
 * that page to say. Every analytics product works this way; the difference is
 * whether it admits it.
 *
 * So: the snippet, with this deployment's own address already in it, and a
 * plain statement of what works without it.
 */
import { useState } from 'react';
import api from '../../lib/api.js';
import { useTracking, copy } from '../../lib/urltracking.js';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, CardBody } from '../../components/Primitives.jsx';
import { num } from './TrackBits.jsx';

export default function BeaconPanel({ compact }) {
  const { data } = useTracking(() => api.urlTracking.setup());
  const [copied, setCopied] = useState(false);

  if (!data) return null;

  const { location, dwell } = data;
  const local = /localhost|127\.0\.0\.1/.test(dwell.beaconUrl);

  const take = async () => {
    const ok = await copy(dwell.snippet);
    setCopied(ok);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Card>
      <CardHead icon="wand" tone="g-amber" title="Measuring location and time on page" />
      <CardBody>
        <div className="utsetup">
          <div>
            <h4>Where the visitor is</h4>
            <p className="utnote">{location.how}.</p>
            <p className="utnote">
              Located <b>{num(location.located)}</b> of {num(location.clicks)} clicks so far.
              {location.provider
                ? ' Addresses not covered by an edge header are looked up, cached for a day.'
                : ' Behind Cloudflare or a similar edge this fills in on its own. Otherwise set'
                  + ' GEO_PROVIDER=ipapi in backend/.env to look addresses up — that sends visitor'
                  + ' IPs to a third party, so it is off until somebody chooses it.'}
            </p>
          </div>

          <div>
            <h4>How long they stayed</h4>
            <p className="utnote">
              This cannot be measured from here — after the redirect the visitor is on a page this
              server does not serve, and no browser reports on those. Paste this into the destination
              page and visits arriving through a tracking link will report how long they lasted:
            </p>

            <code className="utpreview">{dwell.snippet}</code>

            <div className="utqracts">
              <button type="button" className={`btn${copied ? ' ok' : ''}`} onClick={take}>
                <Icon name={copied ? 'check' : 'file'} />{copied ? 'Copied!' : 'Copy snippet'}
              </button>
              <span className="dim">
                Measured on {num(dwell.measured)} of {num(dwell.clicks)} clicks.
              </span>
            </div>

            {local ? (
              <p className="utbad">
                That address is localhost, so the snippet only works on a page served from this
                machine. Set PUBLIC_URL in backend/.env before putting it on a live site.
              </p>
            ) : null}

            {compact ? null : (
              <p className="utnote">
                It counts only the time the page is actually visible, reports on the way out, and
                does nothing at all for visitors who did not arrive through a tracking link. Clicks
                to a destination you do not control keep working exactly as before — they simply
                report no time, rather than a guess.
              </p>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
